import { CpuChipIcon, RectangleGroupIcon } from "@heroicons/react/24/solid";
import { SimpleContainerOverview } from "components/Environment/shared/ContainerOverview";
import React, { useState } from "react";
import PortsIcon from "assets/img/svg/ports.svg?react";
import WireIcon from "assets/img/svg/wire.svg?react";
import { GPUInstanceType } from "types/InstanceTypes";
import { classNames, replaceRawWithBlob } from "components/utils";
import {
  InboxStackIcon,
  PencilIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import { Tooltip } from "@mui/material";
import { EditComputeModal } from "components/Launchables/DeployRedesign/EditComputeModal";
import { ViewScriptModal } from "../../../ViewScriptModal";
import { LaunchableComputeOverview } from "./LaunchableComputeOverview";

interface LaunchableConfigurationViewProps {
  intendedInstanceType: string | null;
  intendedDiskStorage: string | null;
  instanceTypeObject: GPUInstanceType | null;
  onSetNewCurrentInstanceType?: (
    instanceTypeObject: GPUInstanceType | null,
    diskStorage: string,
  ) => void;
  loadingInstance: boolean;
  diskStorage: string | null;
  file: string | null;
  shortFileName: string;
  python: string | null;
  cuda: string | null;
  containerSelected: ContainerSelected | null;
  forceJupyterInstall: boolean | null;
  ports: Record<string, string> | null;
  firewallRules: CreateFirewallRule[] | null;
  disabled?: boolean;
  lifecycleScript?: string | null;
  hideEditCompute?: boolean;
}

const LaunchableConfigurationView: React.FC<
  LaunchableConfigurationViewProps
> = ({
  intendedInstanceType,
  intendedDiskStorage,
  instanceTypeObject,
  onSetNewCurrentInstanceType,
  loadingInstance,
  diskStorage,
  file,
  shortFileName,
  python,
  cuda,
  containerSelected,
  forceJupyterInstall,
  ports,
  firewallRules,
  disabled,
  lifecycleScript,
  hideEditCompute,
}) => {
  const [showEditComputeModal, setShowEditComputeModal] = useState(false);
  const [showViewScriptModal, setShowViewScriptModal] = useState(false);

  return (
    <div className="flex flex text-gray-900 dark:text-white overflow-x-auto">
      <EditComputeModal
        intendedInstanceType={intendedInstanceType}
        currentInstanceType={instanceTypeObject}
        show={showEditComputeModal}
        setShow={setShowEditComputeModal}
        onSuccess={(selectedInstanceType: GPUInstanceType, storage: string) => {
          setShowEditComputeModal(false);
          if (onSetNewCurrentInstanceType) {
            onSetNewCurrentInstanceType(
              selectedInstanceType,
              selectedInstanceType?.elastic_root_volume
                ? storage || "120"
                : selectedInstanceType?.supported_storage
                  ? selectedInstanceType.supported_storage[0].size
                  : "120",
            );
          }
        }}
      />
      <ViewScriptModal
        lifecycleScript={lifecycleScript || ""}
        show={showViewScriptModal}
        setShow={setShowViewScriptModal}
      />
      <div className="flex flex-row w-full justify-start">
        <div>
          <div className="flex flex-row items-center mb-1">
            <CpuChipIcon className="h-6 w-6 mr-1" />
            <span>Compute</span>
            {!hideEditCompute && (
              <Tooltip
                title={
                  onSetNewCurrentInstanceType
                    ? "Edit compute"
                    : "Login to Edit to Compute"
                }
                placement="top"
              >
                <span
                  className="text-xs text-highlight hover:text-highlightLighter cursor-pointer ml-2"
                  onClick={() => {
                    if (onSetNewCurrentInstanceType) {
                      setShowEditComputeModal(true);
                    }
                  }}
                >
                  <PencilIcon className="h-4 w-4" />
                </span>
              </Tooltip>
            )}
          </div>
          <LaunchableComputeOverview
            intendedInstanceType={intendedInstanceType}
            intendedDiskStorage={intendedDiskStorage}
            instanceTypeObject={instanceTypeObject}
            onSetNewCurrentInstanceType={onSetNewCurrentInstanceType}
            loadingInstance={loadingInstance}
            diskStorage={diskStorage}
          />
        </div>
        <div className="text-white mr-3 items-center flex ml-3 mr-5">
          <WireIcon width={50} height={50} />
        </div>
        <div className="flex flex-col">
          <div className="flex flex-row items-center mb-2">
            <RectangleGroupIcon className="h-6 w-6 mr-1" />
            <span>Container</span>
          </div>
          <div>
            <SimpleContainerOverview
              pythonVersion={python || ""}
              cudaVersion={cuda || ""}
              containerSelected={containerSelected}
            />
          </div>
          <div className="flex flex-col mt-2">
            {forceJupyterInstall && (
              <span className="text-xs">- Preinstalls Jupyter</span>
            )}
            {lifecycleScript && (
              <span className="mt-1 text-xs">
                - Post-installs a{" "}
                <span
                  className="text-highlight underline cursor-pointer"
                  onClick={() => setShowViewScriptModal(true)}
                >
                  script
                </span>
              </span>
            )}
          </div>
        </div>
        {file && (
          <>
            <div className="text-white mr-3 items-center flex ml-5 mr-5">
              <WireIcon width={50} height={50} />
            </div>
            <div className="flex flex-col text-sm">
              <div className="flex flex-row items-center">
                <InboxStackIcon className="h-6 w-6 mr-1" />
                <span>Files</span>
              </div>
              <div className="flex flex-row mt-1 w-max">
                <span className="text-sm">{shortFileName}</span>
              </div>
              <div className="flex flex-row mt-1">
                <span
                  className="text-sm hover:underline text-highlight cursor-pointer"
                  onClick={() => {
                    if (disabled) return;
                    window.open(
                      replaceRawWithBlob(file) || "",
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                >
                  View Source
                </span>
              </div>
            </div>
          </>
        )}
        {((ports && Object.keys(ports).length > 0) ||
          (firewallRules && firewallRules.length > 0)) && (
          <>
            <div className="text-white mr-3 items-center flex ml-3 mr-3">
              <WireIcon width={50} height={50} />
            </div>
            <div
              className={classNames(
                "flex flex-col  items-center text-gray-500 dark:text-white",
              )}
            >
              <div className="flex flex-row mt-[2px] items-center">
                <PortsIcon height={"14px"} width={"32px"} />
                <div className="flex flex-row">
                  <span className="text-sm ml-1">Exposed Ports</span>
                </div>
              </div>
              <div className="flex flex-col">
                {ports && Object.keys(ports).length > 0 && (
                  <div className="flex flex-row w-full mt-1">
                    <span className="text-xs font-bold mr-1">Tunnels:</span>
                    <span className="text-xs max-w-[250px]">
                      {Object.keys(ports)
                        .map((port) => `${port}:${ports[port]}`)
                        .join(" | ")}
                    </span>
                  </div>
                )}
                {firewallRules && firewallRules.length > 0 && (
                  <div className="flex flex-row w-full mt-1 justify-start">
                    <span className="text-xs font-bold mr-1">
                      Firewall Rules:
                    </span>
                    <span className="text-xs max-w-[250px] flex flex-row">
                      {firewallRules.map((firewallRule, index) => {
                        if (firewallRule.allowedIPs === "user-ip") {
                          return (
                            <>
                              {index > 0 && (
                                <span className="text-xs mr-1">
                                  {firewallRule.allowedIPs.length > 0
                                    ? " , "
                                    : ""}
                                </span>
                              )}
                              <Tooltip
                                title="Only the user's IP address will be allowed to access this port(s)"
                                placement="top"
                              >
                                <span className="flex flex-row items-start cursor-pointer">
                                  <span className="h-[12px] flex flex-row justify-start text-xs">
                                    {firewallRule.port}
                                  </span>
                                  <div className="flex flex-row items-center mt-[2px]">
                                    <UserIcon className="w-3 h-3 ml-1" />
                                  </div>
                                </span>
                              </Tooltip>
                            </>
                          );
                        } else {
                          return (
                            <>
                              {index > 0 && (
                                <span className="text-xs mr-1">
                                  {firewallRule.allowedIPs.length > 0
                                    ? " , "
                                    : ""}
                                </span>
                              )}
                              <p className="flex flex-row items-center">
                                {firewallRule.port}
                              </p>
                            </>
                          );
                        }
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LaunchableConfigurationView;
